import React, { useCallback, useEffect, useState } from 'react';

import { Edge, SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';

import { RouteProp, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectScreenReaderEnabled } from 'store/config/selectors';
import { setEpgSelectedLayout, setFocusToNow } from 'store/epg/actions';
import { EpgLayout } from 'store/epg/reducer.types';
import { getEpgSelectedLayout } from 'store/epg/selectors';
import { endSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';

import useHeaderConfig from 'utils/hooks/useHeaderConfig';

import EpgGridContent from './components/EpgGridContent';
import EpgHeader from './components/EpgHeader';
import EpgListContent from './components/EpgListContent';
import useFiltering from './hooks/useFiltering';
import styles from './styles';

export const edges: Edge[] = ['left', 'right'];

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.EPG>;
    route: RouteProp<RouteNavigationParams, SCREEN.EPG>;
};

function Epg({ navigation, route }: Props) {
    const [focused, setFocused] = useState(false);

    const dispatch = useDispatch();
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);
    const epgSelectedLayout = useSelector(getEpgSelectedLayout);

    const filterProps = useFiltering(screenReaderEnabled);

    const [selectedLayout, setSelectedLayout] = React.useState<
        EpgLayout | undefined
    >(epgSelectedLayout);

    const handleChangeEpgLayout = useCallback(
        (newLayout: EpgLayout) => {
            dispatch(setEpgSelectedLayout(newLayout));
        },
        [dispatch],
    );

    useHeaderConfig({
        isRoot: true,
        screenFocused: focused,
        disableFocusOnMount: selectedLayout === EpgLayout.LIST,
    });

    useFocusEffect(
        useCallback(() => {
            dispatch(
                endSentryTransaction(SENTRY_TRANSACTIONS.PageNavigationEPG),
            );
            setFocused(true);

            return () => {
                // before navigating to home or to detail pages, clear some data stored in lists and cancel debounce items
                setFocused(false);
                dispatch(setFocusToNow(false));
            };
        }, []),
    );

    useEffect(() => {
        setSelectedLayout(
            screenReaderEnabled
                ? EpgLayout.LIST
                : epgSelectedLayout ?? EpgLayout.GRID,
        );
    }, [epgSelectedLayout, screenReaderEnabled]);

    return (
        <SafeAreaView style={styles.safeAreaView} edges={edges}>
            <EpgHeader
                screenReaderEnabled={screenReaderEnabled}
                epgSelectedLayout={selectedLayout}
                onChangeEpgLayout={handleChangeEpgLayout}
                {...filterProps}
            />
            {selectedLayout === EpgLayout.GRID && (
                <EpgGridContent navigation={navigation} route={route} />
            )}
            {selectedLayout === EpgLayout.LIST && (
                <EpgListContent
                    selectedFilterCategory={filterProps.selectedFilterCategory}
                />
            )}
        </SafeAreaView>
    );
}

export default Epg;
